import React from 'react';
import { Award, Star, Zap, BarChart3, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

// Composants existants
import CTA from '../components/CTA';
import GoogleReviewsBox from '../components/GoogleReviewsBox';
import PageSpeedBox from '../components/PageSpeedBox';
import DigitalScoreCta from '../components/DigitalScoreCta';
import CmsInfoBox from '../components/CmsInfoBox';
import KeywordAnalysisReadOnly from '../components/KeywordAnalysisReadOnly';
import AIRecommendationsCard from '../components/AIRecommendationsCard';
import ExitPopupDemo from '../components/ExitPopupDemo';
import QuizCompletionCta from '../components/QuizCompletionCta';

// Services et hooks
import { useTestPageData } from '../features/testPage/hooks/useTestPageData';

// Types
interface Prospect {
  id?: string;
  prenom?: string;
  nom?: string;
  site_url?: string;
  score?: number;
  quiz_fait?: boolean;
  quiz_commence?: boolean;
  token?: string;
  metadata?: {
    company?: string;
    domain_emelia?: string;
    industry?: string;
    location?: string;
    companyDescription?: string;
  };
}

// Utilisation de l'interface existante de pagespeedData

// Utilitaires
const extractDomain = (url: string): string => {
  if (!url) return '';
  try {
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    }
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  } catch (error) {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  }
};

const getDomainFromProspect = (prospect: Prospect | null): string => {
  if (!prospect) return '';
  return prospect.site_url || prospect.metadata?.domain_emelia || '';
};

const formatPageSpeedScore = (score: number | null | undefined, fallback: number = 0): number => {
  if (score === null || score === undefined) return fallback;
  if (score <= 1) return Math.round(score * 100);
  return Math.round(score);
};

const getAnimationDelay = (index: number, baseDelay: number = 100): string => {
  return `${baseDelay + index * 100}ms`;
};

const splitIntoParagraphs = (text: string): string[] => {
  if (!text) return [];
  return text.split(/\n\s*\n/).filter(p => p.trim().length > 0).map(p => p.trim());
};

/**
 * Page de test refactorisée avec une architecture modulaire
 */
export const TestPageRefactored: React.FC = () => {
  const {
    // États
    loading,
    isLoadingAi,
    isLoadingIntro,
    prospect,
    cmsData,
    pagespeedData,
    // keywordsData, // Non utilisé
    aiRecommendations,
    personalizedIntro,
    error,
    aiError,
    keyComponentKey,
    mockData,
    
    // Actions
    refreshAIRecommendations,
    startQuiz,
    refreshGoogleReviews,
    forceRegenerateIntro,
    // updateState // Non utilisé
  } = useTestPageData();



  // Rendu conditionnel pour les erreurs
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Erreur</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-center">
            <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
              <div className="flex-shrink-0">
                <img src="/images/smart_impact_logo.jpg" alt="Smart Impact" className="h-12 object-contain" />
              </div>
              <div className="text-center">
                <p className="text-gray-600 text-xs sm:text-sm font-medium">
                  Agence digitale • Référencement • IA • Développement
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-12 lg:pt-20 lg:pb-16">
        <div className="absolute inset-0 z-0 opacity-30">
          <div className="absolute right-0 top-0 -translate-y-1/4 translate-x-1/4 transform rounded-full blur-3xl filter opacity-70" style={{ width: '50rem', height: '30rem', backgroundColor: '#0f0f0f' }}></div>
          <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 transform rounded-full blur-3xl filter opacity-70" style={{ width: '50rem', height: '30rem', backgroundColor: '#1f1f1f' }}></div>
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <div className="mx-auto max-w-5xl text-center">
            
            <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl animate-fadeSlideUp" style={{animationDelay: '100ms', color: '#0f0f0f'}}>
              <span className="text-[#0f0f0f]">
                Découvrez ce qui freine le trafic de {prospect?.metadata?.company || prospect?.nom || extractDomain(getDomainFromProspect(prospect)) || 'votre entreprise'} ?
              </span>
            </h1>
            
            {/* Introduction personnalisée */}
            {isLoadingIntro ? (
              <div className="mx-auto mb-8 max-w-2xl text-center animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto"></div>
                <p className="text-sm text-gray-400 mt-2">Génération de votre analyse personnalisée...</p>
              </div>
            ) : personalizedIntro ? (
              <div className="mx-auto mb-8 max-w-2xl text-center">
                {splitIntoParagraphs(personalizedIntro).map((paragraph: string, index: number) => (
                  <p key={index} className="mb-4 text-lg animate-fadeSlideUp" 
                     style={{animationDelay: getAnimationDelay(index, 200), color: '#6b7280'}}>
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              <p className="mx-auto mb-8 max-w-2xl text-lg animate-fadeSlideUp" 
                 style={{animationDelay: '200ms', color: '#6b7280'}} data-component-name="TestPageRefactored">
                Notre analyse est en préparation. Consultez les résultats détaillés ci-dessous en attendant.
              </p>
            )}
            
            {/* Site URL avec style accentué */}
            <div className="mt-2 mb-6 animate-fadeSlideUp" style={{animationDelay: '250ms'}}>
              <div className="inline-block relative">
                <div className="relative z-10 font-mono text-xl md:text-2xl font-bold py-2 px-4 bg-white rounded-lg shadow-md transform rotate-[-1deg]">
                  <span className="text-[#0f0f0f]">
                    {loading ? 'Chargement...' : 
                      error ? 'site-web.com' : 
                      getDomainFromProspect(prospect)
                    }
                  </span>
                  <div className="absolute -bottom-1 -left-1 -right-1 h-1 rounded-full" style={{ backgroundColor: '#eb811c' }}></div>
                </div>
                <div className="absolute -bottom-1.5 left-1 right-1 h-2 blur-md z-0 rounded-full" style={{ backgroundColor: '#ef9a4a33' }}></div>
              </div>
            </div>
            
            {/* Badge de performance */}
            <div className="flex justify-center mb-8 animate-fadeSlideUp" style={{animationDelay: '300ms'}}>
              <div className="inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium shadow-sm" style={{ backgroundColor: '#fef3e2', color: '#0f0f0f' }}>
                <span className="mr-2 inline-block">
                  <Star size={14} />
                </span>
                Score Global {prospect?.score || mockData.digitalScore}%
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <main className="relative z-10 container mx-auto px-4 pb-20">
        <div className="mx-auto max-w-5xl">
          {/* Badge rapport d'analyse */}
          <div className="flex justify-center my-12">
            <div className="inline-flex items-center justify-center rounded-full px-6 py-3 text-base font-medium animate-fadeSlideUp shadow-sm" style={{ backgroundColor: '#fef3e2', color: '#0f0f0f' }}>
              <span className="mr-2 inline-block">
                <Award size={18} />
              </span>
              {loading ? 'Chargement...' : 
                aiError ? 'Check-up digital gratuit' : 
                `Check-up digital gratuit de ${prospect?.site_url ? extractDomain(prospect.site_url) : 'votre entreprise'}`
              }
            </div>
          </div>

          {/* Section analyse du site */}
          <section className="max-w-4xl mx-auto mt-12 mb-12 flex flex-col md:flex-row gap-6">
            {/* PageSpeed Insights Box */}
            <div className="md:w-1/2 bg-white rounded-lg shadow-md p-6 flex flex-col justify-between h-full min-h-[615px]">
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2 flex items-center" style={{ color: '#0f0f0f' }}>
                  <Zap className="mr-2" color="#eb811c" /> 
                  PageSpeed Insights
                </h2>
                <p style={{ color: '#6b7280' }}>
                  Vitesse de chargement de votre site web
                </p>
              </div>
              
              <div className="flex-grow">
                <PageSpeedBox 
                  mobileScore={formatPageSpeedScore(pagespeedData?.mobile_performance_score, mockData.pageSpeed.mobile)} 
                  desktopScore={formatPageSpeedScore(pagespeedData?.desktop_performance_score, mockData.pageSpeed.desktop)}
                  isLoading={false}
                  prospectSector={prospect?.metadata?.industry || "d'activités"}
                  pagespeedData={pagespeedData ? {
                    performance_score: pagespeedData.mobile_performance_score || 0,
                    accessibility_score: pagespeedData.accessibility_score || 0,
                    best_practices_score: pagespeedData.best_practices_score || 0,
                    seo_score: pagespeedData.seo_score || 0,
                    mobile_performance_score: pagespeedData.mobile_performance_score,
                    desktop_performance_score: pagespeedData.desktop_performance_score,
                    mobile_data: pagespeedData.mobile_data,
                    desktop_data: pagespeedData.desktop_data
                  } : null}
                />
              </div>
            </div>
            
            {/* Avis Google Business */}
            <div className="md:w-1/2 bg-white rounded-lg shadow-md p-6 flex flex-col justify-between h-full min-h-[615px]">
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <div 
                    className="cursor-pointer hover:scale-110 transition-transform mr-2"
                    style={{ color: '#eb811c' }}
                    onClick={() => {
                      refreshGoogleReviews();
                      // Forcer un rafraîchissement de la page après un court délai
                      setTimeout(() => window.location.reload(), 500);
                    }}
                    title="Actualiser les avis Google"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" fill="currentColor"/>
                      <path d="M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6Z" fill="white"/>
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold" style={{ color: '#0f0f0f' }}>
                    Avis Google Business
                  </h2>
                </div>
                <p style={{ color: '#6b7280' }}>
                  Réputation en ligne et avis clients
                </p>
              </div>
              
              <div className="flex-grow">
                <GoogleReviewsBox 
                  key={`google-reviews-${keyComponentKey}`}
                  businessName={prospect?.metadata?.company || prospect?.nom || 'Entreprise'}
                  location={prospect?.metadata?.location}
                  prospectId={prospect?.id || ''}
                  domain={getDomainFromProspect(prospect)}
                />
              </div>
            </div>
          </section>
          
          {/* Digital Score CTA */}
          <div className="mb-8 animate-fadeSlideUp" style={{animationDelay: '350ms'}}>
            <div className="mx-auto max-w-4xl bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md">
              <div className="p-1 animate-pulse" style={{backgroundColor: '#eb811c'}}></div>
              <div className="p-6">
                <DigitalScoreCta 
                  onClick={startQuiz}
                  className="transform hover:translate-y-[-2px] transition-transform duration-300"
                  companyName={prospect?.metadata?.company || prospect?.nom || extractDomain(getDomainFromProspect(prospect))}
                  prospectId={prospect?.id}
                />
              </div>
            </div>
          </div>
          
          {/* CMS Info */}
          <div className="mb-12 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md animate-fadeSlideUp" style={{animationDelay: '400ms'}}>
            <div className="p-1" style={{backgroundColor: '#eb811c'}}></div>
            <div className="p-6">
              <CmsInfoBox cmsData={cmsData} />
            </div>
          </div>
          
          {/* Keywords Performance */}
          <div className="mb-12 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md animate-fadeSlideUp" style={{animationDelay: '500ms'}}>
            <div className="p-1" style={{backgroundColor: '#eb811c'}}></div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-4" style={{ backgroundColor: '#eb811c1a', color: '#eb811c' }}>
                    <BarChart3 size={20} />
                  </div>
                  <h3 className="text-lg font-semibold" style={{ color: '#0f0f0f' }}>Performance des Mots-clés dans Google Suisse</h3>
                </div>
                <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ color: '#6b7280', backgroundColor: '#fef3e2' }}>Analyse SEO</span>
              </div>
              <KeywordAnalysisReadOnly 
                domain={getDomainFromProspect(prospect)} 
                prospectId={prospect?.id} 
              />
            </div>
          </div>
          
          {/* AI Recommendations */}
          <div className="mb-12 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md animate-fadeSlideUp" style={{animationDelay: '600ms'}}>
            <div className="p-1" style={{backgroundColor: '#eb811c'}}></div>
            <div className="p-6">
              <AIRecommendationsCard
                recommendations={aiRecommendations.length > 0 ? aiRecommendations : mockData.recommendations}
                isLoading={isLoadingAi}
                error={aiError}
                onRefresh={refreshAIRecommendations}
              />
            </div>
          </div>
          
          {/* Quiz Completion CTA */}
          <QuizCompletionCta prospect={prospect} />
        </div>
      </main>

      {/* Footer */}
      {prospect && (
        <footer className="mt-12 mb-4 text-center max-w-2xl mx-auto">
          <p className="text-xs text-gray-500">
            Cette fiche est issue d'une analyse basée uniquement sur des données publiques et des tendances sectorielles pour {prospect.site_url ? extractDomain(prospect.site_url) : 'cette entreprise'}. 
            Elle ne contient aucune information personnelle ni confidentielle, et respecte pleinement les réglementations suisses et européennes sur la protection des données.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Son seul but : offrir aux dirigeants une lecture neutre et utile de leur présence numérique. Cette page est volontairement exclue des moteurs de recherche.
          </p>
        </footer>
      )}

      {/* Exit Popup */}
      {prospect && prospect.id && (
        <ExitPopupDemo 
          prospectId={prospect.id} 
          options={{
            sensitivity: 20,
            delay: 1000,
            onlyOnce: true
          }}
        />
      )}


    </div>
  );
};

export default TestPageRefactored;
